import { getSocketUrl } from "@/lib/utils.ts";
import type { GetAccountContextResp } from "@/types/accounts.ts";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { getSocketAuthRetryDelayMs, isRetryableSocketAuthError } from "@/shared/authFailure.ts";
import type { ClientToServerEvents, ServerToClientEvents } from "../types/socketIo.ts";

export type SocketConnectionState = "connecting" | "connected" | "disconnected" | "error";

interface SocketContextValue {
  socket: SocketIoClientSocket | null;
}

const SocketContext = createContext<SocketContextValue | null>(null);

/** Type of a socket.io socket - client side only. */
export type SocketIoClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Top level socket provider for the app. Should not have any business logic for specific threads, etc,
 * just manages the socket connection and provides it to the app.
 */
export const SocketProvider = ({
  children,
  userContext,
}: {
  children: React.ReactNode;
  userContext: GetAccountContextResp | null;
}) => {
  const socketRef = useRef<SocketIoClientSocket | null>(null);
  const authRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authRetryCountRef = useRef(0);
  const [connectionState, setConnectionState] = useState<SocketConnectionState>("connecting");

  useEffect(() => {
    // Don't initialize socket until we have user context
    if (!userContext) {
      return;
    }

    // Prevent duplicate connections in React Strict Mode
    if (socketRef.current) {
      return;
    }

    setConnectionState("connecting");

    // Initialize socket connection
    const socketURL = getSocketUrl();
    const socket: SocketIoClientSocket = io(socketURL.origin, {
      path: socketURL.pathname,
      withCredentials: true,
    });

    socketRef.current = socket;

    const clearAuthRetry = () => {
      if (authRetryTimerRef.current !== null) clearTimeout(authRetryTimerRef.current);
      authRetryTimerRef.current = null;
    };

    const handleConnect = () => {
      clearAuthRetry();
      authRetryCountRef.current = 0;
      console.log("Socket.IO connected:", socket.id);
      setConnectionState("connected");
    };
    socket.on("connect", handleConnect);

    const handleConnectError = (error: Error) => {
      console.warn("Socket.IO connection error:", error);
      setConnectionState("error");
      if (!isRetryableSocketAuthError(error) || socketRef.current !== socket) return;

      clearAuthRetry();
      const delay = getSocketAuthRetryDelayMs(authRetryCountRef.current);
      authRetryCountRef.current += 1;
      authRetryTimerRef.current = setTimeout(() => {
        authRetryTimerRef.current = null;
        if (socketRef.current === socket && !socket.connected) socket.connect();
      }, delay);
    };
    socket.on("connect_error", handleConnectError);

    const handleDisconnect = (reason: Socket.DisconnectReason) => {
      console.log("Socket.IO disconnected:", reason);
      setConnectionState("disconnected");
      // The server disconnects sockets whenever it ends their session or deletes
      // their account, and on an account switch, so they re-auth with the
      // current cookies. socket.io-client deliberately does not
      // auto-reconnect after a server-initiated disconnect, so reconnect manually.
      // This does NOT risk a reconnect loop once the session is dead: the auth middleware rejects
      // the reconnect with a CONNECT_ERROR packet, which destroys the socket and
      // closes the Manager with skipReconnect=true — a single attempt, not backoff
      // retries (the Manager only retries on transport-level failures, i.e. a real
      // network drop, which is the reconnection we do want). Only reconnect if this
      // socket is still the active one — an account-switch effect re-run may be
      // tearing it down to build a fresh socket, and we must not resurrect it.
      if (reason === "io server disconnect" && socketRef.current === socket) {
        socket.connect();
      }
    };
    socket.on("disconnect", handleDisconnect);

    // Event handlers for existing events
    const handlePong = (data: unknown) => {
      console.log("Received pong:", data);
    };
    socket.on("pong", handlePong);

    // catch all for debugging
    const handleOnAny = (event: string, ...args: unknown[]) => {
      console.debug("Catch all received event:", event, args);
    };
    socket.onAny(handleOnAny);

    // Cleanup
    return () => {
      clearAuthRetry();
      authRetryCountRef.current = 0;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current.off("connect", handleConnect);
        socketRef.current.off("connect_error", handleConnectError);
        socketRef.current.off("disconnect", handleDisconnect);
        socketRef.current.off("pong", handlePong);
        socketRef.current.offAny(handleOnAny);
        socketRef.current = null;
        setConnectionState("disconnected");
      }
    };
  }, [userContext]);

  // Only expose the socket if it's connected. Helps ensure dependent useEffects will re-run when the socket is connected,
  // so they don't just try and give up, without having to remember to check connectionState as well.
  const contextSocket = connectionState === "connected" ? socketRef.current : null;

  return (
    <SocketContext.Provider value={{ socket: contextSocket }}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
